import Phaser from 'phaser';
import { Entity, EntityConfig } from '../base/Entity';
import { PlatformBehavior } from '../behaviors/PlatformBehavior';
import { AbductableBehavior, AbductableConfig, WeightClass } from '../behaviors/AbductableBehavior';
import { Allegiance } from '../../constants/enums';

export { WeightClass };

export interface PowerUpConfig extends EntityConfig {
  /** Bio matter value when abducted (default: 0) */
  bioMatter?: number;
  /** Weight class for abduction (default: Light) */
  weight?: WeightClass;
  /** Enable floating animation (default: true) */
  float?: boolean;
  /** Enable pulse animation (default: true) */
  pulse?: boolean;
}

/**
 * PowerUp - Base class for all powerups
 *
 * PowerUps are entities that:
 * - Can be abducted by the player's tractor beam (via AbductableBehavior)
 * - Effects are applied via data-driven system (DataManagerPowerUp + EffectResolver)
 * - Have visual feedback (floating, pulsing animations)
 *
 * The AbductableBehavior handles:
 * - Abduction mechanics (lifting, falling, gravity)
 * - Effect application via EffectResolver
 * - Notification text via DataManagerPowerUp
 * - Entity destruction on completion
 */
export class PowerUp extends Entity {
  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    texture: string,
    config: PowerUpConfig = {}
  ) {
    super(scene, x, y, texture, {
      ...config,
      allegiance: Allegiance.Neutral,
    });

    // Add platform behavior FIRST - required for gravity/falling when abduction is cancelled
    // PlatformBehavior sets state.behaviors.platform which AbductableBehavior checks
    this.addBehavior(new PlatformBehavior(this, {
      gravity: 800,
      maxFallSpeed: 600,
    }));

    // Add abductable behavior - handles all abduction logic
    const abductableConfig: AbductableConfig = {
      bioMatter: config.bioMatter ?? 0,
      weight: config.weight ?? WeightClass.Light,
      canAbductWhileDying: false,
    };
    this.addBehavior(new AbductableBehavior(this, abductableConfig));

    // Asegurar que el powerup quede por encima de estructuras y fondo
    this.setDepth(100);

    // Set initial frame if atlas has named frames (e.g., upHealth_default_0)
    const textureObj = scene.textures.get(texture);
    const frames = textureObj.getFrameNames().filter(f => f !== '__BASE');
    if (frames.length > 0) {
      const defaultFrame = frames.find(f => f.endsWith('_default_0')) || frames[0];
      this.setFrame(defaultFrame);
    }

    // Make powerups 1.5x bigger for better visibility
    this.setScale(1.5);

    // Add visual animations
    if (config.float !== false) {
      this.addFloatAnimation(4, 800);
    }
    if (config.pulse !== false) {
      this.addPulseAnimation(1.15, 600);
    }
  }

  /**
   * Add floating animation (vertical oscillation)
   */
  addFloatAnimation(amplitude: number = 5, duration: number = 1000): void {
    this.scene.tweens.add({
      targets: this,
      y: this.y - amplitude,
      duration: duration,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  /**
   * Add pulse animation (scale oscillation)
   */
  addPulseAnimation(scale: number = 1.1, duration: number = 500): void {
    this.scene.tweens.add({
      targets: this,
      scaleX: scale,
      scaleY: scale,
      duration: duration,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }
}
